/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable no-nested-ternary */
import React, { useCallback } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/table';
import { Flex, Box, Text, ScaleFade, Spinner, Menu, MenuButton, MenuList, MenuItem, Portal, Stack } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { useTable, useSortBy, Column } from 'react-table';
import { TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted } from 'react-icons/ti';
import { GoPrimitiveDot } from 'react-icons/go';
import { v4 as uuidv4 } from 'uuid';
import { BsThreeDotsVertical } from 'react-icons/bs';
import {
  mainTable,
  sortIconWrapper,
  tableHeadingNonSticky,
} from '../recruiterManagement/recruiterDashboard/recruitersInfoTable/RecruiterInfoTableStyle';
import { TeamMemberStateEnum } from './useTeamMembers';
import { TeamMemberAction } from './TeamType';
import { useStoreState } from '../../../models/hooks';
import colors from '../../../styles/colors';
import { usePermissionGroup } from './teamPermission/usePermissionGroup';
import TeamPermissionModal from './teamPermission/TeamPermissionModal';
import { RemoteConfigKey } from '../../../firebase/remoteConfig/keys';

export type TeamInfoTableViewProps<Data extends Record<string, unknown>> = {
  data: Data[];
  columns: Column<Data>[];
  isLoading: TeamMemberStateEnum;
  handleTeamMember: (teamMember: Data, action: TeamMemberAction) => void;
};

export default function TeamInfoTableView<Data extends Record<string, unknown>>({
  data,
  columns,
  isLoading,
  handleTeamMember,
}: TeamInfoTableViewProps<Data>) {
  const { t } = useTranslation('team');
  const { accounts } = useStoreState((state) => state.app);
  const [selectRecruiter, setSelectRecruiter] = React.useState<string | null>(null);
  const isRecruiterPermissionAssignmentEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_RECRUITER_PERMISSION_ASSIGNMENT];
  });

  const owners = accounts?.owners ?? [];
  const superOwners = accounts?.superOwners ?? [];

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: 'name',
            desc: false,
          },
        ],
      },
    },
    useSortBy,
  );

  const isAdmin = useCallback((userId: string) => owners.includes(userId), [owners]);

  const isSuperAdmin = useCallback((userId: string) => superOwners.includes(userId), [superOwners]);

  // const { currentUserProfile } = useCurrentUserProfile();

  // const canChangeRole = (): boolean => {
  //   if (!currentUserProfile?.id) {
  //     return false;
  //   }

  //   const { superOwners, owners } = accounts ?? /* istanbul ignore next */ {};

  //   return (
  //     (superOwners?.includes(currentUserProfile.id) || owners?.includes(currentUserProfile.id)) ??
  //     /* istanbul ignore next */ false
  //   );
  // };

  const { permissionGroups } = usePermissionGroup();

  return (
    <>
      <ScaleFade initialScale={0.7} in unmountOnExit={false}>
        <Table {...getTableProps()} css={mainTable} className="mainTable gray-table" key="mainTable">
          <Thead css={tableHeadingNonSticky} className="tableHeading" pl="4">
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()} textAlign="left" key={headerGroup.getHeaderGroupProps().key}>
                {headerGroup.headers.map((column) => (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    isNumeric={column.isNumeric}
                    key={column.getHeaderProps().key}
                    paddingLeft="1rem"
                  >
                    <Box style={{ display: 'flex', maxWidth: '100%' }}>
                      <Text display="inline">{column.render('Header')}</Text>
                      <Box pl="4" display="inline" css={sortIconWrapper} className="sortIconWrapper">
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {column.render('Header') !== 'Options' ? (
                          column.isSorted ? (
                            // istanbul ignore next
                            column.isSortedDesc ? (
                              <TiArrowSortedUp aria-label="sorted descending" color="#BCC2CE" style={{ display: 'inline' }} />
                            ) : (
                              <TiArrowSortedDown aria-label="sorted ascending" color="#BCC2CE" style={{ display: 'inline' }} />
                            )
                          ) : (
                            <TiArrowUnsorted color="#BCC2CE" style={{ display: 'inline' }} data-testid="sortedAscendingBtn" />
                          )
                        ) : null}
                      </Box>
                    </Box>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {isLoading === TeamMemberStateEnum.LOADING ? (
              <Tr>
                <Td colSpan={4} style={{ padding: '20px', textAlign: 'center' }} data-testid="loadingSpinner">
                  <Spinner />
                </Td>
              </Tr>
            ) : rows.length > 0 ? (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <Tr
                    {...row.getRowProps()}
                    style={{ height: '40px' }}
                    key={row.getRowProps().key}
                    cursor="pointer"
                    _hover={{ background: 'lightgrey', opacity: '0.95' }}
                  >
                    {row.cells.map((cell) => (
                      <Td
                        {...cell.getCellProps()}
                        isNumeric={cell.column.isNumeric}
                        pr=""
                        key={cell.getCellProps().key}
                        paddingLeft="1rem"
                        py={3}
                      >
                        {cell.column.id === 'name' && (
                          <Box>
                            <Text className="team-member-name-cell name" data-testid="teamNameCell">
                              {cell.render('Cell')}
                            </Text>
                            <Stack spacing={0.5}>
                              {cell.row.original.restrictPositions ? (
                                <Text color="blue.500" fontSize="sm">
                                  {t('restrictedRecruiter')}
                                </Text>
                              ) : null}
                              {isSuperAdmin(cell.row.original.id as string) ? (
                                <Text color="blue.500" fontSize="sm">
                                  {t('superAdmin')}
                                </Text>
                              ) : isAdmin(cell.row.original.id as string) ? (
                                <Text color="blue.500" fontSize="sm">
                                  {t('admin')}
                                </Text>
                              ) : null}
                            </Stack>
                            {isRecruiterPermissionAssignmentEnabled && (
                              <Box variant="unstyled" display="table">
                                {permissionGroups
                                  ?.filter((group) =>
                                    group.permissions?.some((p) => (cell.row.original.permissions as string[])?.includes(p.key)),
                                  )
                                  ?.map((group) => (
                                    <Box display="table-row" verticalAlign="top" key={group.heading}>
                                      <Box display="table-cell" padding="0 !important">
                                        <Text fontSize="xs" color={colors.gray[500]} paddingRight={2}>
                                          {group.heading} :
                                        </Text>
                                      </Box>
                                      <Box display="table-cell">
                                        <Stack spacing={0.3}>
                                          {group?.permissions
                                            ?.filter((p) => (cell.row.original.permissions as string[])?.includes(p.key))
                                            ?.map((p) => (
                                              <Text fontSize="xs" color={colors.gray[500]} key={p.key}>
                                                {p.info}
                                              </Text>
                                            ))}
                                        </Stack>
                                        {/* {(cell.row.original.permissions as string[])?.includes(
                                          group?.permissions?.find((p) => p.key?.toLowerCase()?.includes('admin'))?.key as string,
                                        ) ? (
                                          <Text fontSize="xs" color={colors.gray[500]}>
                                            {t('hasAdminAccess')}
                                          </Text>
                                        ) : (
                                          
                                        )} */}
                                      </Box>
                                    </Box>
                                  ))}
                              </Box>
                            )}
                          </Box>
                        )}

                        {cell.column.id === 'status' && (
                          <Text
                            className="team-member-status"
                            d="flex"
                            alignItems="center"
                            textTransform="capitalize"
                            color="green.500"
                          >
                            <GoPrimitiveDot />
                            {cell.value}
                          </Text>
                        )}
                        {cell.column.id === 'action' && (
                          <Flex justifyContent="end">
                            <TeamMemberActionMenu
                              userId={cell.row.original.id as string}
                              isAdmin={Boolean(isAdmin(cell.row.original.id as string))}
                              isSuperAdmin={Boolean(isSuperAdmin(cell.row.original.id as string))}
                              onRemove={() => {
                                handleTeamMember(cell.row.original, TeamMemberAction.DELETE);
                              }}
                              onChangeAdminRole={() => {
                                handleTeamMember(
                                  cell.row.original,
                                  isAdmin(cell.row.original.id as string) ? TeamMemberAction.DEMOTE : TeamMemberAction.PROMOTE,
                                );
                              }}
                              onPermissionClick={() => {
                                setSelectRecruiter(cell.row.original.id as string);
                              }}
                              isRestrictedPosition={cell.row.original.restrictPositions as boolean}
                              onRestrictUser={() => {
                                handleTeamMember(
                                  cell.row.original,
                                  cell.row.original.restrictPositions ? TeamMemberAction.UNRESTRICT : TeamMemberAction.RESTRICT,
                                );
                              }}
                            />
                          </Flex>
                        )}
                      </Td>
                    ))}
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td colSpan={4} style={{ padding: '20px', textAlign: 'center' }}>
                  <Text>{t('noData')}</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </ScaleFade>
      <TeamPermissionModal
        recruiterId={selectRecruiter!}
        isOpen={!!selectRecruiter}
        onClose={() => {
          setSelectRecruiter(null);
        }}
        permissions={(data?.find((teamMember) => teamMember.id === selectRecruiter)?.permissions as string[]) || []}
      />
    </>
  );
}

interface TeamMemberActionMenuProps {
  onRemove: () => void;
  onChangeAdminRole: () => void;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  onPermissionClick: () => void;
  userId: string;
  isRestrictedPosition: boolean;
  onRestrictUser: () => void;
}

function TeamMemberActionMenu({
  onRemove,
  isAdmin,
  isSuperAdmin,
  onChangeAdminRole,
  onPermissionClick,
  userId,
  isRestrictedPosition,
  onRestrictUser,
}: TeamMemberActionMenuProps) {
  const { t } = useTranslation('team');
  const isRecruiterPermissionAssignmentEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_RECRUITER_PERMISSION_ASSIGNMENT];
  });

  return (
    <Menu closeOnSelect={false} key={uuidv4()}>
      <MenuButton
        px={2}
        py={2}
        transition="all 0.2s"
        borderRadius="md"
        _hover={{ bg: 'gray.400' }}
        _focus={{ boxShadow: 'outline', backgroundColor: 'none' }}
        key={uuidv4()}
        data-testid={`teamMemberActionMenuBtn-${userId}`}
      >
        <BsThreeDotsVertical />
      </MenuButton>
      <Portal>
        <MenuList key={uuidv4()} zIndex={1000} style={{ zIndex: 1000 }}>
          {isRecruiterPermissionAssignmentEnabled && (
            <MenuItem fontSize="sm" py={2} onClick={onPermissionClick}>
              {t('menuItems.allowPermissions')}
            </MenuItem>
          )}
          <MenuItem fontSize="sm" py={2} onClick={onChangeAdminRole} data-testid={`TeamAction-${userId}`}>
            {isAdmin ? t('menuItems.revokeAdmin') : t('menuItems.makeAdmin')}
          </MenuItem>
          <MenuItem fontSize="sm" py={2} onClick={onRemove} data-testid={`TeamDelete-${userId}`}>
            {t('menuItems.removeAccount')}
          </MenuItem>

          {!isAdmin && !isSuperAdmin && (
            <MenuItem fontSize="sm" py={2} onClick={onRestrictUser} data-testid={`TeamRestrict-${userId}`}>
              {isRestrictedPosition ? t('menuItems.unRestrictUserPosition') : t('menuItems.restrictUserPosition')}
            </MenuItem>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
}
